import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardTitle,
    IonCheckbox,
    IonCol,
    IonContent,
    IonPage,
    IonRow,
} from "@ionic/react";
import React, { useContext, useEffect, useState } from "react";
import {Auditor, Event, OrganizationAuditor, EventFilter, Organization, EventAuditor, EventAuditorFilter} from "../../../../../models";
import Header from "../../../../../components/Headers/Header";
import { RouteComponentProps } from "react-router";
import PageTitle from "../../../../../components/PageTitle/PageTitle";
import ErrorAlert from "../../../../../components/Errors/ErrorAlert";
import SelectOrganizationEvent from "../../../../../components/Event/SelectOrganizationEvent";
import Spinner from "../../../../../components/Spinners/Spinner";
import { AuditorStatus, CreateAuditorInput, CreateEventAuditorFilterInput, CreateOrganizationAuditorInput } from "../../../../../API";
import { PersonContext } from "../../../../../context/PersonContext";
import FileBrowser from "../../../../../components/FileBrowser/FileBrowser";
import { createAuditor, getAuditorByPhoneNumber } from "../../../../../utilities/auditor/Auditor";
import SuccessBanner from "../../../../../components/Banners/SuccessBanner";
import { formatTwilioNumber } from "../../../../../utilities/contact/FormatPhoneNumber";
import { sendOrganizationWelcomeTexts} from "../../../../../utilities/twilio/AuditorMessages";
import { handleBulkUploadFromCSVForOrganization } from "../../../../../utilities/twilio/BulkAddSubscribers";
import { Table } from "reactstrap";
import PhoneInput from "react-phone-number-input";
import { getOrganizationById } from "../../../../../utilities/organization/Organization";
import { createOrganizationAuditor, getOrganizationAuditorByPhoneNumber, getOrganizationAuditorsWithFilterByOrganizationId } from "../../../../../utilities/organizationAuditor/OrganizationAuditor";
import { getEventAuditorsByEventId } from "../../../../../utilities/eventAuditor/EventAuditor";
import { getEventFiltersByOrganizationId } from "../../../../../utilities/eventFilter/EventFilter";
import { createEventAuditorFilter } from "../../../../../utilities/eventAuditorFilter/EventAuditorFilter";

interface OrgPageProps extends RouteComponentProps<{
    id: string;
}> {}

export interface orgAuditorFormattedOption {
    isChecked: boolean
    isDisabled: boolean
    invalidReason?: string
    label: string
    phoneNumber: string
    name: string
    id?: string
    orgAuditor?: OrganizationAuditor
    filters?: string
    rolesOptionString?: string
    daysOptionString?: string
    sectionOptionString?: string
}

const EventOrganizationBulkAddSubscribersPage: React.FC<OrgPageProps> = ({match}) => {
    const user = useContext(PersonContext);

    const [usePastEvent, setUsePastEvent] = useState(true);

    const [organization, setOrganization] = useState<Organization>(); 
    const [organizationFilters, setOrganizationFilters] = useState<EventFilter[] | null | undefined>();
    const [currentOrganizationAuditors, setCurrentOrganizationAuditors] = useState<OrganizationAuditor[] | null | undefined>();
    const [formattedOptions, setFormattedOptions] = useState<orgAuditorFormattedOption[] | null | undefined>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [progressMessage, setProgressMessage] = useState("");

    const handleSelectedFile = async (file: File) => {
        setIsLoading(true);
        setSuccess("");
        setError("");
        if (organization) {
            const result = await handleBulkUploadFromCSVForOrganization(file, organization, setProgressMessage, currentOrganizationAuditors);
            if (result.isSuccess) {
                const options: orgAuditorFormattedOption[] = result.result;
                setFormattedOptions(options);
                setSuccess("Successfully uploaded the file.");
            } else {
                setError(result.message);
            }
        } else {
            setError("No event was found.");
        }
        setIsLoading(false);
    }

    async function getOrganization() {
        const queryResult = await getOrganizationById(match.params.id);
        if (queryResult.isSuccess) {
            const organization: Organization = queryResult.result;
            setOrganization(organization);
            await getOrganizationSubscribers(organization);
            await getOrganizationFilters(organization);
        } else {
            setError("Sorry, a problem occurred. Please go back and try again.");
        }
    }

    async function getOrganizationFilters(org: Organization) {
        const queryResult = await getEventFiltersByOrganizationId(org.id);
        if (queryResult.isSuccess) {
            const orgFilters: EventFilter[] = queryResult.result;
            setOrganizationFilters(orgFilters);
        }
    }

    const getOrganizationSubscribers = async (org: Organization) => {
        if (org) {
            const queryResult = await getOrganizationAuditorsWithFilterByOrganizationId(org.id);
            if (queryResult.isSuccess) {
                const orgAuditorList: OrganizationAuditor[] = queryResult.result;
                setCurrentOrganizationAuditors(orgAuditorList);
                return orgAuditorList;
            }
        }
    }

    useEffect(() => {
        getOrganization();
    }, [match.params.id]);

    const handleSelectedEvent = async (newlySelectedEvent: Event) => {
        setIsLoading(true);
        setSuccess("");
        setError("");
        let eventSubscribers: EventAuditor[] = [];
        const eventSubscribersQuery = await getEventAuditorsByEventId(newlySelectedEvent.id);
        if (eventSubscribersQuery.isSuccess) {
            eventSubscribers = eventSubscribersQuery.result;
        }

        formatEventAuditorOptions(eventSubscribers);

        setIsLoading(false);
    }

    const formatEventAuditorOptions = (possibleNewAuditors: EventAuditor[]) => {
        const auditorList = currentOrganizationAuditors || [];
        // Track the seen phone numbers to avoid duplicates
        const seenPhoneNumbers: string[] = [];
        let result: orgAuditorFormattedOption[] = [];
        for (let i = 0; i < possibleNewAuditors.length; i++) {
            const possibleNewAuditor = possibleNewAuditors[i];

            const message = i+1 + " of " + possibleNewAuditors.length;
            setProgressMessage(message);

            let label = possibleNewAuditor.name + ": " + possibleNewAuditor.phoneNumber;
            let isDisabled = false;
            let invalidReason = "";

            // If there is a current list of event auditors, the list was checked, and a duplicate was found, add to the label
            const foundAuditor = auditorList.findIndex(eventAuditor => eventAuditor.phoneNumber === possibleNewAuditor.phoneNumber);
            if (foundAuditor > -1) {
                invalidReason = "Already subscribed to this event.";
                label = label + invalidReason;
                isDisabled = true;
            }
            
            // If this phone number is a duplicate of another in the list of auditors from a past event, add to the label
            const foundPhoneNumber = seenPhoneNumbers.findIndex(ph => ph === possibleNewAuditor.phoneNumber);
            if (foundPhoneNumber > -1) {
                invalidReason = "This phone number is a duplicate of another number in the list.";
                label = label + invalidReason;
                isDisabled = true;
            } else {
                seenPhoneNumbers.push(possibleNewAuditor.phoneNumber || "");
            }

            let filtersString: string | undefined = undefined;
            const filtersItems: any = possibleNewAuditor.filters;
            if (filtersItems) {
                const filtersArray: (EventAuditorFilter | null)[] | undefined | null = filtersItems.items;
                if (filtersArray && filtersArray.length > 0) {
                    filtersString = "";
                    filtersArray.forEach((filter: any) => {
                        if (filter && filter.filter.name === "Roles") {
                            const options = filter.options;
                            if (options && options.length > 0) {
                                options.forEach((option: string | undefined) => {
                                    if (option) {
                                        filtersString = filtersString + " " + option;
                                    }
                                })
                            }
                        }
                    })
                }
            }
            
            const formatted: orgAuditorFormattedOption = {
                isChecked: false,
                isDisabled: isDisabled,
                label: label,
                phoneNumber: possibleNewAuditor.phoneNumber || "",
                name: possibleNewAuditor.name || "",
                invalidReason: invalidReason,
                id: possibleNewAuditor.id,
                orgAuditor: possibleNewAuditor,
                filters: "",
            };

            result.push(formatted);
        };
        setProgressMessage("");
        setFormattedOptions(result);
    }

    const handleSelectAll = (value: boolean) => {
        setIsLoading(true);
        if (formattedOptions) {
            let list: orgAuditorFormattedOption[] = (formattedOptions ? [...formattedOptions] : []);
            if (list) {
                for (let i = 0; i < list.length; i++) {
                    const current = list[i];
                    if (!current.isDisabled) current.isChecked = value;
                }
            } else {
                setError("Could not find the subscribers.");
            }
            setFormattedOptions(list);
        } 
        
        setIsLoading(false);
    }

    const handleOptionClick = (index: number) => {
        if (formattedOptions) {
            const selectedOption: orgAuditorFormattedOption = formattedOptions[index];

            const updatedFormattedOption: orgAuditorFormattedOption = {
                ...selectedOption,
                isChecked: !selectedOption.isChecked
            };

            const updatedList = [
                ...formattedOptions?.slice(0, index),
                updatedFormattedOption,
                ...formattedOptions.slice(index + 1)
            ];
            setFormattedOptions(updatedList);
        }
    }

    const handleSubmit = async () => {
        setIsLoading(true);
        setSuccess("");
        setError("");

        if (formattedOptions) {
            for (let i = 0; i < formattedOptions.length; i++) {
                const current = formattedOptions[i];

                const message = i+1 + " of " + formattedOptions.length;
                setProgressMessage(message);

                if (current.isChecked && !current.isDisabled) {
                    // Find or Create an auditor
                    let auditor: Auditor | null | undefined = current.orgAuditor?.auditor;
                    let isNewAuditor = false;
                    if (!auditor) {
                        const auditorQueryResult = await getAuditorByPhoneNumber(current.phoneNumber);
                        if (auditorQueryResult.isSuccess) {
                            auditor = auditorQueryResult.result;
                        } else {
                            const auditorInput: CreateAuditorInput = {
                                name: current.name,
                                phoneNumber: formatTwilioNumber(current.phoneNumber),
                                status: AuditorStatus.unverified,
                                createdBy: user.id
                            };
                            const createAuditorResult = await createAuditor(auditorInput);
                            if (createAuditorResult.isSuccess) {
                                auditor = createAuditorResult.result;
                                isNewAuditor = true;
                            }
                        }
                    }
                    
                    if (organization) {
                        // Check to make sure this phone number is not already used for this organization
                        const eventAuditorQueryResult = await getOrganizationAuditorByPhoneNumber(current.phoneNumber, organization?.id);
                        if (!eventAuditorQueryResult.isSuccess) {
                            // Create New Event Auditor
                            const input: CreateOrganizationAuditorInput = {
                                organizationId: organization?.id,
                                organizationAuditorOrganizationId: organization?.id,
                                auditorId: auditor ? auditor.id : "",
                                name: current.name,
                                phoneNumber: formatTwilioNumber(current.phoneNumber),
                                status: auditor?.status || AuditorStatus.unverified,
                                createdBy: user.id,
                                lastUpdatedBy: user.id
                            };
                            const createResult = await createOrganizationAuditor(input);
                            const newOrganizationAuditor: OrganizationAuditor = createResult.result;

                            // To Do - add filters to the organization auditor
                            if (organizationFilters) {
                                for (let j = 0; j < organizationFilters.length; j++) {
                                    const currentFilter: EventFilter = organizationFilters[j];
                                    if (currentFilter) {
                                        // Assume all options to begin with
                                        let optionsStringArray: (string | null)[] = currentFilter.options || [];

                                        // If the filter is a role filter, add the roles from that event auditor to this organization auditor
                                        if (currentFilter.name?.includes("Role")) {
                                            if (current.rolesOptionString) {
                                                optionsStringArray = current.rolesOptionString.split(";");
                                            }
                                        }

                                        // Add the event filter with the selected options
                                        const eventAuditorFilterInput: CreateEventAuditorFilterInput = {
                                            organizationId: organization?.id,
                                            organizationAuditorId: newOrganizationAuditor.id,
                                            filterId: currentFilter.id,
                                            options: optionsStringArray,
                                            createdBy: user.id
                                        };
                                        await createEventAuditorFilter(eventAuditorFilterInput);
                                    }
                                }
                            }

                            // To Do - send opt in texts to the new organization auditor
                            if (auditor) sendOrganizationWelcomeTexts(auditor, organization, true);
                        }
                    }
                }
            }
        }
        setProgressMessage("");
        setSuccess("Successfully added new subscribers to your event!");
        setIsLoading(false);
    }

    const handleImportTypeChange = (isPastEvent: boolean) => {
        setUsePastEvent(isPastEvent);
        setIsLoading(false);
        setFormattedOptions(null);
        setError("");
        setSuccess("");
        setProgressMessage("");
    }

    return (
        <IonPage className="bg-light">
            <Header />
            <IonContent>
                <PageTitle title={organization ? organization.name : "alerts"} />
                {organization && (
                    <>
                        {error && <ErrorAlert width="12" error={error} />}
                        {success && <SuccessBanner width="12" success={success} />}
                        <IonRow id="bulkAdd" className="ion-align-items-stretch">
                            <IonCol size="12">
                                <IonCard mode="md" className="ion-padding bg-white stretch">
                                    <IonCardTitle>Bulk Add</IonCardTitle>
                                    <IonCardContent>
                                        <IonRow className="ion-justify-content-center">
                                            <IonCol sizeXs="12" sizeSm="6" className="ion-text-center">
                                                <IonButton color={usePastEvent ? "primary" : "light"} onClick={() => handleImportTypeChange(true)}>
                                                    Use Organization Event
                                                </IonButton>
                                            </IonCol>
                                            <IonCol sizeXs="12" sizeSm="6" className="ion-text-center">
                                                <IonButton color={usePastEvent ? "light" : "primary"} onClick={() => handleImportTypeChange(false)}>
                                                    Use CSV
                                                </IonButton>
                                            </IonCol>
                                        </IonRow>
                                        {usePastEvent ?
                                            <IonRow>
                                                <IonCol>
                                                    <p>First, select a past event to see the subscribers you can choose from.</p>
                                                    {(organization && organization.id) && (
                                                        <SelectOrganizationEvent organizationId={organization.id} onSelect={handleSelectedEvent} />
                                                    )}
                                                </IonCol>
                                            </IonRow>
                                            :
                                            <>
                                                <IonRow className="ion-justify-content-center">
                                                    <IonCol size="12" className="text-center">
                                                        <p>First, select a CSV file or Excel file with a column labeled Name and a column labeled Phone Number.</p>
                                                    </IonCol>
                                                </IonRow>
                                                <IonRow className="ion-justify-content-center">
                                                    <IonCol size="12" className="text-center">
                                                        <FileBrowser handleSelectedFile={handleSelectedFile}/>
                                                    </IonCol>
                                                </IonRow>
                                            </>
                                        }
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>
                        </IonRow>
                        <IonRow id="bulkAdd" className="ion-align-items-stretch">
                            <IonCol size="12">
                                <IonCard mode="md" className="ion-padding bg-white stretch">
                                    <IonCardTitle>Select Subscribers</IonCardTitle>
                                        {isLoading ?
                                            <>
                                                <Spinner />
                                                <p className="ion-text-center ion-text-wrap">{progressMessage}</p>
                                            </>
                                            :
                                            <IonCardContent>
                                            <IonRow>
                                                <IonCol size="12">
                                                    <p>Select subscribers to add. Phone numbers already signed up for {organization.name || "the current organization"} are disabled.</p>
                                                </IonCol>
                                            </IonRow>
                                            <IonRow>
                                                <IonCol size="12">
                                                    <p>Each subscriber you add will be signed up for ALL filters.</p>
                                                </IonCol>
                                            </IonRow>
                                            <IonRow>
                                                <IonCol size="12">
                                                    <p>When you add them, each subscriber will get a text letting them know how to edit their alert settings.</p>
                                                </IonCol>
                                            </IonRow>
                                            <IonRow className="ion-justify-content-center">
                                                <IonCol sizeXs="12" sizeMd="6" className="ion-text-center">
                                                    <IonButton color="primary" onClick={() => handleSelectAll(true)}>Select All</IonButton>
                                                </IonCol>
                                                <IonCol sizeXs="12" sizeMd="6" className="ion-text-center">
                                                    <IonButton color="light" onClick={() => handleSelectAll(false)}>Deselect All</IonButton>
                                                </IonCol>
                                            </IonRow>
                                            {formattedOptions ?
                                                <Table responsive hover bordered>
                                                    <thead>
                                                        <tr>
                                                            <th></th>
                                                            <th>Select</th>
                                                            <th>Name</th>
                                                            <th>Phone Number</th>
                                                            <th>Valid?</th>
                                                            <th>Filters</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {formattedOptions.map((formattedOption, index) => (
                                                            <tr key={index} color={formattedOption.isDisabled ? "warning" : ""}>
                                                                <td className="ion-text-wrap">{index+1}</td>
                                                                <td>
                                                                    <IonCheckbox disabled={formattedOption.isDisabled } checked={formattedOption.isChecked} onClick={() => handleOptionClick(index)} />
                                                                </td>
                                                                <td className="ion-text-wrap">{formattedOption.name}</td>
                                                                <td className="ion-text-wrap">
                                                                    {formattedOption.phoneNumber ?
                                                                        <PhoneInput
                                                                            placeholder="No phone number found"
                                                                            defaultCountry="US"
                                                                            disabled={true}
                                                                            value={formattedOption.phoneNumber as any}
                                                                            onChange={(phoneNumberValue?: any) => {
                                                                                setError("");
                                                                            }}
                                                                        />
                                                                        :
                                                                        <p></p>
                                                                    }
                                                                </td>
                                                                <td className={`ion-text-wrap ${formattedOption.invalidReason ? "text-danger" : "text-success"}`}>{formattedOption.invalidReason ? formattedOption.invalidReason : "valid"}</td>
                                                                <td className="ion-text-wrap">{formattedOption.filters ? formattedOption.filters : "all"}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                                :
                                                <p>None found.</p>
                                            }
                                            <IonRow className="ion-justify-content-center">
                                                <IonCol size="12" className="ion-text-center">
                                                    <IonButton color="success" onClick={handleSubmit}>Submit</IonButton>
                                                </IonCol>
                                            </IonRow>
                                        </IonCardContent>
                                        }
                                </IonCard>
                            </IonCol>
                        </IonRow>
                    </>
                )}           
            </IonContent>
        </IonPage>
    );
};

export default EventOrganizationBulkAddSubscribersPage;